import i18n from "@/i18n";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

/** Common Day.js format patterns */
export const DATE_FORMAT_ISO = "YYYY-MM-DD";
export const TIME_FORMAT_HH_MM = "HH:mm";

// Configure Day.js with timezone, localized format, and advanced format support
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);

/**
 * Gets the user's timezone from the browser.
 * Falls back to 'America/New_York' if detection fails.
 *
 * @returns The IANA timezone identifier (e.g., 'America/New_York', 'America/Los_Angeles')
 */
export const getUserTimezone = (): string => {
  try {
    return dayjs.tz.guess();
  } catch (error) {
    console.warn("Failed to detect timezone, falling back to America/New_York", error);
    return "America/New_York";
  }
};

/**
 * Utility function to get the start of the day in the local timezone
 *
 * @param date Date for which to get the start of the day Date.
 * @returns The start of the day Date in the local timezone.
 */
export const getStartOfDayInLocalTimezone = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Utility function that returns a new Date with the specified number of days added to the input date.
 *
 * @param date Date to add days to.
 * @returns A new Date with the specified number of days added to the input date.
 */
export const addDaysToDate = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Utility function that returns a new Date with the specified number of hours added to the input date.
 *
 * @param date Date to add hours to.
 * @returns A new Date with the specified number of hours added to the input date.
 */
export const addHoursToDate = (date: Date, hours: number): Date => {
  const result = new Date(date);
  result.setHours(result.getHours() + hours);
  return result;
};

/**
 * Formats a date in the user's detected timezone with the specified Day.js format string.
 *
 * @param date Date to format (can be Date object, ISO string, or timestamp)
 * @param format Day.js format string (default: 'LLL' for long localized date-time)
 * @returns Formatted date string in user's timezone
 *
 * @example
 * formatDateInUserTimezone(new Date('2024-01-15T20:30:00Z'), 'LLL')
 * // Returns: "January 15, 2024 3:30 PM" (in user's timezone)
 */
export const formatDateInUserTimezone = (date: Date | string, format = "LLL"): string => {
  const userTimezone = getUserTimezone();
  return dayjs(date).tz(userTimezone).format(format);
};

/**
 * Gets the timezone abbreviation for the user's detected timezone.
 * Uses short format (e.g., "EST", "PST", "CST")
 *
 * @param date Optional date to get abbreviation for (handles DST changes)
 * @returns Timezone abbreviation (e.g., "EST", "EDT", "PST", "PDT")
 */
export const getTimezoneAbbreviation = (date?: Date | string): string => {
  const userTimezone = getUserTimezone();
  const dateToUse = date || new Date();
  return dayjs(dateToUse).tz(userTimezone).format("z");
};

/**
 * Formats an alert timestamp in the user's detected timezone.
 * Uses the long localized date-time format (LLL): "MMMM D, YYYY h:mm A"
 * Includes timezone abbreviation for clarity.
 *
 * @param date Alert timestamp to format (can be undefined)
 * @returns Formatted alert timestamp with timezone suffix, or fallback text if undefined
 *
 * @example
 * formatAlertTimestamp('2024-01-15T20:30:00Z')
 * // Returns: "January 15, 2024 3:30 PM (EST)" (if user is in Eastern timezone)
 * // Returns: "January 15, 2024 12:30 PM (PST)" (if user is in Pacific timezone)
 * formatAlertTimestamp(undefined)
 * // Returns: "No timestamp available"
 */
export const formatAlertTimestamp = (date: Date | string | undefined): string => {
  if (!date) {
    return i18n.t("common.noTimestampAvailable");
  }
  const formattedDate = formatDateInUserTimezone(date, "LLL");
  const timezone = getTimezoneAbbreviation(date);
  return `${formattedDate} (${timezone})`;
};

/**
 * Formats an alert timestamp with optional submission time if sync delay > 1 hour.
 * If task creation and observation submission times differ by more than 1 hour,
 * displays both: "Task datetime (submitted: observation datetime)".
 * Otherwise displays only the task creation time.
 *
 * @param taskAuthoredOn Task creation timestamp (required)
 * @param observationEffectiveDateTime Patient submission timestamp (optional)
 * @param format Day.js format string (default: 'LLL' for long localized date-time)
 * @returns Formatted timestamp string, potentially with submission time appended
 *
 * @example
 * // Within 1 hour window
 * formatAlertTimestampWithSubmission('2024-01-15T20:30:00Z', '2024-01-15T20:15:00Z')
 * // Returns: "January 15, 2024 3:30 PM (EST)"
 *
 * // Outside 1 hour window (sync delay)
 * formatAlertTimestampWithSubmission('2024-01-15T20:30:00Z', '2024-01-15T18:00:00Z')
 * // Returns: "January 15, 2024 3:30 PM (submitted: January 15, 2024 1:00 PM)"
 */
export const formatAlertTimestampWithSubmission = (
  taskAuthoredOn: Date | string | undefined,
  observationEffectiveDateTime: Date | string | undefined,
  format = "LLL"
): string => {
  if (!taskAuthoredOn) {
    return i18n.t("common.noTimestampAvailable");
  }

  const formattedTaskTime = formatDateInUserTimezone(taskAuthoredOn, format);
  const timezone = getTimezoneAbbreviation(taskAuthoredOn);

  // If no observation time, just return task time with timezone
  if (!observationEffectiveDateTime) {
    return `${formattedTaskTime} (${timezone})`;
  }

  // Calculate time difference in milliseconds
  const taskTime = dayjs(taskAuthoredOn);
  const obsTime = dayjs(observationEffectiveDateTime);
  const diffInHours = Math.abs(taskTime.diff(obsTime, "hour", true));

  // If within 1 hour window, only show task time
  if (diffInHours <= 1) {
    return `${formattedTaskTime} (${timezone})`;
  }

  // Outside 1 hour window - show both times
  const formattedObsTime = formatDateInUserTimezone(observationEffectiveDateTime, format);
  return i18n.t("common.alertTimestampWithSubmission", {
    taskTime: formattedTaskTime,
    submittedTime: formattedObsTime,
  });
};

/**
 * Formats a date for progress note display in the user's detected timezone.
 * Uses format: "MMMM D, YYYY" (e.g., "January 15, 2024")
 *
 * @param date Date to format (can be undefined)
 * @returns Formatted date string in user's timezone, or empty string if undefined
 *
 * @example
 * formatProgressNoteDate('2024-01-15T20:30:00Z')
 * // Returns: "January 15, 2024" (in user's timezone)
 * formatProgressNoteDate(undefined)
 * // Returns: ""
 */
export const formatProgressNoteDate = (date: Date | string | undefined, format?: string): string => {
  if (!date) {
    return "";
  }
  return formatDateInUserTimezone(date, format ?? "MMMM D, YYYY");
};
